import React, { createContext, useContext, useEffect, useState } from 'react'
import { api, login as apiLogin, loginMfa as apiLoginMfa } from '../lib/api'
import { clearToken, getToken, setToken } from '../lib/auth'
import type { Me } from '../lib/types'

// login() returns this instead of a Me when the account has MFA enabled —
// Login.tsx branches on 'mfaRequired' in the result to show the code-entry
// step instead of navigating, with no access token issued yet.
export interface MfaChallenge {
  mfaRequired: true
  mfaChallengeToken: string
}

interface AuthState {
  me: Me | null
  loading: boolean
  login: (email: string, password: string) => Promise<Me | MfaChallenge>
  // Completes a login that returned an MfaChallenge — pass a fresh TOTP code
  // or a recovery code, never both.
  completeMfaLogin: (mfaChallengeToken: string, payload: { code?: string; recovery_code?: string }) => Promise<Me>
  loginWithToken: (token: string) => Promise<Me>
  logout: () => void
  // Re-fetches /me and updates the cached `me` in place — needed after any
  // action that flips a server-computed flag `me` already holds (e.g.
  // provider_onboarding_complete right after finishing onboarding), since
  // nothing else re-triggers a fetch until the next full login.
  refreshMe: () => Promise<Me>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)

  async function refresh() {
    if (!getToken()) {
      setMe(null)
      setLoading(false)
      return
    }
    try {
      const data = await api('/me')
      setMe(data)
    } catch {
      setMe(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  async function login(email: string, password: string) {
    const result = await apiLogin(email, password)
    if (result.mfa_required) {
      return { mfaRequired: true, mfaChallengeToken: result.mfa_challenge_token! } as MfaChallenge
    }
    return loginWithToken(result.access_token!)
  }

  async function completeMfaLogin(mfaChallengeToken: string, payload: { code?: string; recovery_code?: string }) {
    const result = await apiLoginMfa({ mfa_challenge_token: mfaChallengeToken, ...payload })
    return loginWithToken(result.access_token!)
  }

  // For flows that already have a valid access_token (e.g. invite acceptance,
  // which returns one directly so the new user doesn't have to immediately
  // re-type credentials they just chose) — updates the same in-memory `me`
  // state `login()` does, which plain localStorage writes never would.
  async function loginWithToken(token: string) {
    setToken(token)
    const data = await api('/me')
    setMe(data)
    return data as Me
  }

  function logout() {
    clearToken()
    setMe(null)
    api('/auth/logout', { method: 'POST' }).catch(() => {})
  }

  async function refreshMe() {
    const data = await api('/me')
    setMe(data)
    return data as Me
  }

  return <AuthContext.Provider value={{ me, loading, login, completeMfaLogin, loginWithToken, logout, refreshMe }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
